import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  MapPin,
  ArrowRight,
  Loader2,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  API,
  setUserToken,
  setStoredUser,
} from "@/lib/api";

export default function Register() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showExtra, setShowExtra] = useState(false);
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    phone: "",
    city: "",
    referral_code: "",
  });

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) {
      setForm((s) => ({ ...s, referral_code: ref.toUpperCase() }));
      setShowExtra(true);
    }
  }, [searchParams]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || !form.name) {
      toast.error("Email, password, and name are required");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/register`, form, {
        withCredentials: true,
      });
      setUserToken(data.session_token);
      setStoredUser(data.user);
      toast.success(
        data.user.role === "owner"
          ? "Account created — you are the owner"
          : data.user.points
          ? `Welcome ${data.user.name}! +${data.user.points} points from referral 🎉`
          : `Account created — welcome, ${data.user.name}`
      );
      const role = data.user.role;
      nav(["owner", "editor", "viewer"].includes(role) ? "/admin" : "/app", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign up failed");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl
    )}`;
  };

  return (
    <div className="min-h-screen bg-grid flex items-center justify-center p-6">
      <div
        data-testid="register-card"
        className="w-full max-w-md bg-card rounded-2xl shadow-[0_10px_40px_-10px_rgba(255,107,0,0.15)] border border-border p-8 animate-in-up"
      >
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-xl bg-primary flex items-center justify-center shadow-lg shadow-primary/20">
            <MapPin className="w-6 h-6 text-primary-foreground" strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              NearMe
            </div>
            <div className="text-lg font-bold leading-tight">Create account</div>
          </div>
        </div>

        <h1 className="text-3xl font-extrabold tracking-tight mb-1">Get started</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Sign up to save favorites, earn rewards and manage NearMe.
        </p>

        <Button
          type="button"
          variant="outline"
          onClick={googleLogin}
          data-testid="google-signup-btn"
          className="w-full h-11 mb-4 font-semibold"
        >
          <ShieldCheck className="w-4 h-4 mr-2 text-primary" />
          Continue with Google
        </Button>
        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-card px-2 text-xs uppercase tracking-widest text-muted-foreground">
              or with email
            </span>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label className="text-sm font-semibold">Name *</Label>
              <Input
                data-testid="register-name-input"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                className="mt-1.5 h-11"
                autoComplete="name"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold">Email *</Label>
              <Input
                type="email"
                data-testid="register-email-input"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className="mt-1.5 h-11"
                autoComplete="email"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold">Password *</Label>
              <Input
                type="password"
                data-testid="register-password-input"
                value={form.password}
                onChange={(e) => set("password", e.target.value)}
                className="mt-1.5 h-11"
                autoComplete="new-password"
              />
              <p className="text-xs text-muted-foreground mt-1.5">
                At least 6 characters.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowExtra((v) => !v)}
            data-testid="register-toggle-extra"
            className="text-xs font-semibold text-primary hover:underline"
          >
            {showExtra ? "Hide optional fields" : "Add optional details (phone, city)"}
          </button>

          {showExtra && (
            <div className="space-y-3 pt-1 animate-in-up">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Phone
                  </Label>
                  <Input
                    data-testid="register-phone-input"
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    className="mt-1.5 h-11"
                    autoComplete="tel"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    City
                  </Label>
                  <Input
                    data-testid="register-city-input"
                    value={form.city}
                    onChange={(e) => set("city", e.target.value)}
                    className="mt-1.5 h-11"
                    autoComplete="address-level2"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Referral code (optional)
                </Label>
                <Input
                  data-testid="register-referral-input"
                  value={form.referral_code}
                  onChange={(e) => set("referral_code", e.target.value.toUpperCase())}
                  placeholder="e.g. ABC12345"
                  className="mt-1.5 h-11 mono uppercase"
                />
                <p className="text-xs text-muted-foreground mt-1.5">
                  Get 50 bonus points if you were invited.
                </p>
              </div>
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid="register-submit-btn"
            className="w-full h-11 font-semibold group"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                Create account
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" />
              </>
            )}
          </Button>
          <p className="text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link
              to="/login"
              data-testid="link-login"
              className="text-primary font-semibold hover:underline"
            >
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
